import React from 'react';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import CloseIcon from '@material-ui/icons/Close';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import {
  applyTargetOptions,
  applyTargetSelectLabel,
  emptyApplyToSelection,
  promoSelectStyles
} from '../constants';
import { ApplyTarget, ApplyToOptionData, CreatePromoCodePayload } from '../types';
import Modal from '../../shared/components/core-ui/modal/modal.component';
import { Calendar } from '../../shared/components/widgets/calendar/calendar.component';
import api from '../../shared/services/utils/api/api.service';

export function AddPromoCodeModal({
  lookupOptions,
  isLookupsLoading,
  lookupError,
  onClose,
  onCreated
}: {
  lookupOptions: Record<ApplyTarget, ApplyToOptionData[]>;
  isLookupsLoading: boolean;
  lookupError: string;
  onClose: () => void;
  onCreated: (code: string) => Promise<void> | void;
}) {
  const [promoCodeName, setPromoCodeName] = React.useState('');
  const [discountValue, setDiscountValue] = React.useState('');
  const [maximumNumberOfUsagePerUser, setMaximumNumberOfUsagePerUser] = React.useState('0');
  const [availableFrom, setAvailableFrom] = React.useState<Date | null>(null);
  const [availableTo, setAvailableTo] = React.useState<Date | null>(null);
  const [selectedDates, setSelectedDates] = React.useState<Array<[Date, Date]>>([]);
  const [isAutomatic, setIsAutomatic] = React.useState(false);
  const [applyTarget, setApplyTarget] = React.useState<ApplyTarget>('area');
  const [selectedApplyTo, setSelectedApplyTo] =
    React.useState<Record<ApplyTarget, ApplyToOptionData[]>>(emptyApplyToSelection);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');

  const currentOptions = lookupOptions[applyTarget];
  const currentSelectedOptions = selectedApplyTo[applyTarget];

  function handleApplyTargetChange(nextApplyTarget: ApplyTarget) {
    if (nextApplyTarget === applyTarget) {
      return;
    }

    setApplyTarget(nextApplyTarget);
    setSelectedApplyTo(emptyApplyToSelection);
  }

  function handleApplyToChange(value: ValueType<ApplyToOptionData, true>) {
    setSelectedApplyTo((currentSelectedApplyToByTarget) => ({
      ...currentSelectedApplyToByTarget,
      [applyTarget]: value ? [...value] : []
    }));
  }

  function handleDateRangesChange(value: Date | Date[]) {
    setSelectedDates(toDateRanges(value));
  }

  async function handleSubmit() {
    setSubmitError('');

    const discountPercentage = Number(discountValue) / 100;
    const maxUsagePerUser = Number(maximumNumberOfUsagePerUser);

    if (!availableFrom || !availableTo) {
      setSubmitError('Please select the available from and available to dates.');
      return;
    }

    if (availableFrom >= availableTo) {
      setSubmitError('Available to must be after available from.');
      return;
    }

    if (!Number.isInteger(maxUsagePerUser) || maxUsagePerUser < 0) {
      setSubmitError('Maximum uses per user must be a whole number of 0 or more.');
      return;
    }

    const payload: CreatePromoCodePayload = {
      name: promoCodeName,
      maximumNumberOfUsage: 0,
      maximumNumberOfUsagePerUser: maxUsagePerUser,
      availableFrom: availableFrom.getTime(),
      availableTo: availableTo.getTime(),
      ...(selectedDates.length > 0 && {
        dateRanges: selectedDates.map(([from, to]) => ({
          from: from.getTime(),
          to: to.getTime()
        }))
      }),
      type: 'percentage',
      isAutomatic,
      ...(selectedApplyTo.area.length > 0 && {
        areaIds: selectedApplyTo.area.map((option) => option.id)
      }),
      ...(selectedApplyTo.city.length > 0 && {
        cityIds: selectedApplyTo.city.map((option) => option.id)
      }),
      ...(selectedApplyTo.property.length > 0 && {
        propertyIds: selectedApplyTo.property.map((option) => option.id)
      }),
      value: discountPercentage
    };

    setIsSubmitting(true);

    try {
      const response = await api.createPromotion(payload);

      if (!response.ok) {
        setSubmitError('Failed to save promo code. Please try again.');
        return;
      }

      await onCreated(payload.name);
      onClose();
    } catch (error) {
      setSubmitError('Failed to save promo code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      config={{
        showModal: true,
        fullScreen: false,
        showClose: false,
        maxWidth: 'md'
      }}
      eventHandlers={{
        closeModalHandler: onClose
      }}
    >
      <div className="w-full rounded-2xl bg-white px-10 py-12 md:px-16">
        <div className="mb-11 flex items-start justify-between gap-6">
          <h2
            id="add-promo-code-title"
            className="font-recoleta text-3xl font-semibold leading-tight text-neutral-900 md:text-4xl"
          >
            Add new promo code
          </h2>

          <button
            type="button"
            aria-label="Close add promo code modal"
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-neutral-700 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={onClose}
          >
            <CloseIcon className="!h-9 !w-9" />
          </button>
        </div>

        <div className="space-y-9">
          <PromoTextField label="Promo code" value={promoCodeName} onChange={setPromoCodeName} />
          <div className="grid gap-6 sm:grid-cols-2">
            <PromoTextField
              label="Discount value"
              value={discountValue}
              onChange={setDiscountValue}
              type="number"
              min={0}
              step="any"
            />
            <PromoTextField
              label="Maximum uses per user"
              value={maximumNumberOfUsagePerUser}
              onChange={setMaximumNumberOfUsagePerUser}
              type="number"
              min={0}
              step={1}
            />
          </div>
          <div>
            <span className="mb-5 block text-xl font-gilroy-bold text-neutral-800">Apply to</span>
            <div className="grid overflow-hidden rounded-xl border border-gray-200 sm:grid-cols-3">
              {applyTargetOptions.map((option) => (
                <ApplyToOption
                  key={option.value}
                  active={applyTarget === option.value}
                  label={option.label}
                  onClick={() => handleApplyTargetChange(option.value)}
                />
              ))}
            </div>
          </div>

          <div>
            <label
              className="mb-5 block text-xl font-gilroy-bold text-neutral-800"
              htmlFor="apply-to-select"
            >
              {applyTargetSelectLabel[applyTarget]}
            </label>
            <Select<ApplyToOptionData, true>
              inputId="apply-to-select"
              isMulti
              isLoading={isLookupsLoading}
              isDisabled={isLookupsLoading}
              value={currentSelectedOptions}
              options={currentOptions}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.id}
              onChange={handleApplyToChange}
              placeholder={`Search ${applyTargetSelectLabel[applyTarget].toLowerCase()}`}
              noOptionsMessage={() => (isLookupsLoading ? 'Loading...' : 'No options found')}
              className="text-md font-gilroy-medium"
              classNamePrefix="promo-code-select"
              styles={promoSelectStyles}
            />
            {lookupError ? (
              <p className="mt-3 text-base font-gilroy-medium text-red-500">{lookupError}</p>
            ) : null}
          </div>

          <label className="flex items-start gap-4 rounded-xl border border-gray-200 bg-gray-50/70 p-6">
            <input
              type="checkbox"
              className="mt-1 h-6 w-6 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={isAutomatic}
              onChange={(event) => setIsAutomatic(event.target.checked)}
            />
            <span>
              <span className="block text-xl font-gilroy-bold text-neutral-800">
                Apply automatically
              </span>
              <span className="mt-1 block text-base font-gilroy-medium text-neutral-500">
                When a guest books in the city/area/property selected above and supplies no promo
                code of their own, this one is applied for them.
              </span>
            </span>
          </label>

          <section aria-labelledby="promo-availability-title">
            <div className="grid overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:grid-cols-[minmax(18rem,0.75fr)_minmax(0,1.55fr)]">
              <aside className="border-t border-gray-200 bg-gray-50/70 p-6 md:p-8 lg:border-l-0 lg:border-t-0">
                <div className="lg:sticky lg:top-6">
                  <div className="space-y-7">
                    <PromoDateField
                      label="Available from"
                      selected={availableFrom}
                      onChange={setAvailableFrom}
                      minDate={new Date()}
                    />
                    <PromoDateField
                      label="Available to"
                      selected={availableTo}
                      onChange={setAvailableTo}
                      minDate={availableFrom || new Date()}
                    />
                  </div>
                </div>
              </aside>
              <div className="min-w-0 p-6 md:p-8 lg:border-r lg:border-gray-200">
                <div className="mb-6">
                  <h4 className="text-xl font-gilroy-bold text-neutral-800">
                    Applicable date ranges
                  </h4>
                </div>

                <Calendar
                  data={{
                    calenderView: true,
                    basePriceTag: 0,
                    showDateInput: false,
                    multipleRanges: true,
                    dateRanges: selectedDates,
                    minDate: new Date()
                  }}
                  eventHandlers={{
                    onChange: handleDateRangesChange
                  }}
                />
              </div>
            </div>
          </section>

          {submitError ? (
            <div className="rounded-xl border border-red-100 bg-red-50 px-5 py-4 text-lg font-gilroy-medium text-red-600">
              {submitError}
            </div>
          ) : null}

          <div className="flex flex-col-reverse items-stretch justify-end gap-5 pt-16 sm:flex-row sm:items-center">
            <button
              type="button"
              className="inline-flex h-20 items-center justify-center rounded-xl border border-gray-200 bg-white px-12 text-xl font-gilroy-bold text-neutral-900 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex h-20 items-center justify-center rounded-xl bg-primary-600 px-12 text-xl font-gilroy-bold text-white shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Saving...' : 'Save promo code'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function toDateRanges(value: Date | Date[]): Array<[Date, Date]> {
  if (!Array.isArray(value)) {
    return [];
  }

  const ranges: Array<[Date, Date]> = [];

  for (let index = 0; index < value.length; index += 2) {
    const startDate = value[index];
    const endDate = value[index + 1];

    if (startDate instanceof Date && endDate instanceof Date) {
      ranges.push([startDate, endDate]);
    }
  }

  return ranges;
}

function PromoTextField({
  label,
  value,
  onChange,
  type = 'text',
  min,
  step
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number';
  min?: number;
  step?: number | 'any';
}) {
  return (
    <label className="block">
      <span className="mb-5 block text-xl font-gilroy-bold text-neutral-800">{label}</span>
      <span className="flex h-20 items-center rounded-xl border border-gray-200 bg-white px-7 shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2">
        <input
          className="min-w-0 flex-1 bg-transparent text-xl font-gilroy-medium text-neutral-800 outline-none placeholder:text-neutral-300"
          type={type}
          min={min}
          step={step}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
    </label>
  );
}

function PromoDateField({
  label,
  selected,
  onChange,
  minDate
}: {
  label: string;
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
}) {
  return (
    <label className="block">
      <span className="mb-5 block text-xl font-gilroy-bold text-neutral-800">{label}</span>
      <span className="relative block">
        <ReactDatePicker
          selected={selected}
          onChange={(date) => onChange(date as Date | null)}
          minDate={minDate}
          dateFormat="MMM d, yyyy"
          placeholderText="Select date"
          dayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.1rem]'}
          weekDayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-base'}
          className="h-14 w-full rounded-lg border border-gray-200 px-3 pr-14 text-lg font-gilroy-bold outline-none transition-shadow duration-200 placeholder:text-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          calendarClassName="[&_.react-datepicker__current-month]:!text-xl [&_.react-datepicker__current-month]:!font-gilroy-bold [&_.react-datepicker__current-month]:!leading-7 [&_.react-datepicker__current-month]:!text-neutral-800 [&_.react-datepicker-year-header]:!text-xl [&_.react-datepicker-year-header]:!font-gilroy-bold"
          wrapperClassName="block w-full"
          popperClassName="z-[60]"
        />
        <CalendarTodayOutlinedIcon className="pointer-events-none absolute right-3 top-1/2 !h-8 !w-8 -translate-y-1/2 text-neutral-800" />
      </span>
    </label>
  );
}

function ApplyToOption({
  active = false,
  label,
  onClick
}: {
  active?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`flex h-20 items-center justify-center gap-5 border-gray-200 text-xl font-gilroy-medium transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:border-r sm:last:border-r-0 ${
        active ? 'bg-primary-600 text-white' : 'bg-white text-neutral-800 hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
          active ? 'border-white' : 'border-gray-300'
        }`}
      >
        {active ? <span className="h-3 w-3 rounded-full bg-white" /> : null}
      </span>
      {label}
    </button>
  );
}
